import { useQuery } from "@tanstack/react-query";
import { Printer } from "lucide-react";
import { useEffect } from "react";
import { useParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { MAIN_CONTENT_ID, SkipLink } from "@/components/layout/SkipLink";
import { BrandMark } from "@/components/layout/Logo";
import {
  Button,
  ErrorState,
  FullPageSpinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";
import { portal } from "@/lib/api";
import { useEffectiveBranding } from "@/lib/branding";
import { formatDate, formatMoney } from "@/lib/format";
import { countryName } from "@/lib/payments";
import { InvoiceStatusPill } from "@/components/billing/parts";

/**
 * A printable invoice ("PDF ↓", "Download invoice"): opens the browser's print dialog, where it can
 * be saved as a PDF. Rendered without the portal chrome.
 */
export function InvoiceDocumentPage() {
  const { invoiceId = "" } = useParams();
  const { orgId, current } = useOrganization();
  const branding = useEffectiveBranding(orgId);
  const invoice = useQuery({
    queryKey: ["invoice", orgId, invoiceId],
    queryFn: () => portal.invoice(orgId, invoiceId),
  });
  const sub = useQuery({ queryKey: ["subscription", orgId], queryFn: () => portal.subscription(orgId) });
  const ready = invoice.isSuccess && sub.isSuccess;

  useEffect(() => {
    if (!ready) return;
    document.title = `Invoice ${invoice.data.number}`;
    const t = setTimeout(() => window.print(), 300);
    return () => clearTimeout(t);
  }, [ready, invoice.data?.number]);

  if (invoice.isPending || sub.isPending) return <FullPageSpinner />;
  if (invoice.isError) return <ErrorState error={invoice.error} />;
  if (sub.isError) return <ErrorState error={sub.error} />;
  const i = invoice.data;
  const { account } = sub.data;
  const money = (cents: number) => formatMoney(cents, i.currency);

  return (
    <>
      <SkipLink />
      <main
        id={MAIN_CONTENT_ID}
        tabIndex={-1}
        className="mx-auto max-w-3xl bg-surface px-10 py-12 text-fg print:p-0"
      >
        <div className="mb-10 flex items-start justify-between print:hidden">
          <p className="text-sm text-fg-subtle">Use “Save as PDF” in the print dialog to download it.</p>
          <Button
            unstyled
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-muted"
          >
            <Printer className="size-4" aria-hidden /> Print or save as PDF
          </Button>
        </div>

        <header
          className="flex items-start justify-between border-b border-border pb-6"
          style={branding.usesDefaultColour ? undefined : { borderBottomColor: branding.colourHex }}
        >
          <div>
            <p className="text-2xl font-bold">CortexGrip</p>
            <h1 className="mt-1 text-sm font-normal text-fg-subtle">Invoice</h1>
          </div>
          <div className="text-right text-sm">
            <p className="text-lg font-semibold">{i.number}</p>
            <p className="text-fg-muted">Issued {formatDate(i.issued_at)}</p>
            <div className="mt-2">
              <InvoiceStatusPill status={i.status} />
            </div>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 py-6 text-sm">
          <div>
            <p className="mb-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase">Billed to</p>
            <div className="flex items-center gap-2">
              {!branding.usesDefaultLogo && (
                <BrandMark
                  className="size-7 shrink-0 rounded-md"
                  logoUrl={branding.logoUrl}
                  colourHex={branding.colourHex}
                />
              )}
              <p className="font-semibold">{account.company_name || current.organization.name}</p>
            </div>
            {account.billing_email && <p className="text-fg-muted">{account.billing_email}</p>}
            {account.country && <p className="text-fg-muted">{countryName(account.country)}</p>}
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase">Payment</p>
            <p className="text-fg-muted">{i.payment_method_label ?? "—"}</p>
            {i.paid_at && <p className="text-fg-muted">Paid {formatDate(i.paid_at)}</p>}
            {i.failure_reason && <p className="text-[#b8291f]">{i.failure_reason}</p>}
          </div>
        </section>

        <Table className="w-full text-sm">
          <TableHead className="border-y border-border text-left text-xs font-semibold tracking-wide text-fg-subtle uppercase">
            <TableRow>
              <TableHeaderCell className="py-2 font-semibold">Description</TableHeaderCell>
              <TableHeaderCell className="py-2 font-semibold">Period</TableHeaderCell>
              <TableHeaderCell className="py-2 text-right font-semibold">Amount</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow className="border-b border-border">
              <TableCell className="py-3">{i.description}</TableCell>
              <TableCell className="py-3 text-fg-muted">
                {i.period_start ? `${formatDate(i.period_start)} – ${formatDate(i.period_end)}` : "—"}
              </TableCell>
              <TableCell className="py-3 text-right">{money(i.net_cents)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <dl className="mt-4 ml-auto w-64 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <dt className="text-fg-muted">Net</dt>
            <dd>{money(i.net_cents)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-fg-muted">VAT</dt>
            <dd>{money(i.vat_cents)}</dd>
          </div>
          <div className="flex justify-between border-t border-border pt-1.5 font-semibold">
            <dt>Total</dt>
            <dd>{money(i.total_cents)}</dd>
          </div>
        </dl>
      </main>
    </>
  );
}
